import type { CSSProperties, ReactNode } from 'react'

/**
 * The states the prototype never shows, because it only ever holds populated
 * happy paths. Drawn in round two (`1d`), and the rules there matter more than
 * the tiles: three empties that are not interchangeable, skeletons that do not
 * move, and the failure colour reserved for an actual failure.
 */

/**
 * Which of the three empties this is. They look similar and mean completely
 * different things, and the difference is carried by what they say and what
 * they offer — which is why none of them has a decorative glyph tile any more
 * (round two supersedes the original README on that).
 */
export type EmptyKind =
  /** Never had one. Explain what the thing *is*, then the two ways in. */
  | 'first'
  /** Nothing matches. Name the count you *do* have, and offer the filters back. */
  | 'filtered'
  /** Nothing to do, and that is the right answer. Offer nothing. */
  | 'clear'

interface EmptyStateProps {
  kind: EmptyKind
  title: string
  /** What the thing is, or what you do have, or when this changes. */
  note?: ReactNode
  /**
   * `first`: up to two, primary then secondary. `filtered`: the "Clear
   * filters" action chip, never a primary. `clear`: none — a button on a
   * cleared list invents work.
   */
  action?: ReactNode
  /** `filtered` only: the filters actually in force, each one removable. */
  filters?: ReactNode
  style?: CSSProperties
}

export function EmptyState({ kind, title, note, action, filters, style }: EmptyStateProps) {
  return (
    <div
      className="fr"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 12,
        padding: '26px 20px',
        minHeight: 170,
        borderRadius: 14,
        border: '1px solid var(--w-line)',
        background: 'var(--w-pane)',
        boxShadow: 'var(--w-inset),var(--w-lift)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        {/* The one mark any of the three carries, and only the cleared one:
            this is a good state, and the dot is what says so. */}
        {kind === 'clear' && (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 4,
              background: 'var(--color-accent)',
              flex: 'none',
            }}
          />
        )}
        <span style={{ font: '600 13px Manrope,sans-serif' }}>{title}</span>
      </div>

      {note && (
        <span
          style={{
            font: '400 11px/1.65 Manrope,sans-serif',
            color: 'var(--color-muted)',
            maxWidth: 340,
            textWrap: 'pretty',
          }}
        >
          {note}
        </span>
      )}

      {filters && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{filters}</div>}

      {action && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{action}</div>}
    </div>
  )
}

/**
 * One thing in force, with the way to drop it.
 *
 * Written for the `filtered` empty, where the point of the state is that the
 * list is empty *because of these*, so they have to be both visible and
 * removable from where you are. A project's tags are the same control by the
 * same argument — applied to this record, and dropped from where they are
 * shown — so the name is narrower than the thing. Left as FilterChip rather
 * than renamed: it is on twenty call sites and the rename buys nothing.
 */
export function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button
      type="button"
      className="tab"
      onClick={onClear}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '5px 10px',
        borderRadius: 7,
        border: '1px solid var(--w-pill-line)',
        background: 'var(--w-pill-fill)',
        font: '600 11px Manrope,sans-serif',
        color: 'var(--w-pill-ink)',
        cursor: 'pointer',
      }}
    >
      {label}
      <span aria-hidden="true" style={{ color: 'var(--color-muted)' }}>
        ×
      </span>
      <span className="sr-only">— remove this filter</span>
    </button>
  )
}

/**
 * One skeleton block. Give it the height of the thing it stands in for — a
 * 46px table row, a 52px two-line row, a 168px plan card — so the layout
 * doesn't move when the real content arrives.
 *
 * It does not shimmer. See the note on `.w-skel`.
 */
export function Skeleton({
  height,
  width = '100%',
  radius = 99,
  style,
}: {
  height: number | string
  width?: number | string
  radius?: number
  style?: CSSProperties
}) {
  return <div className="w-skel" style={{ height, width, borderRadius: radius, ...style }} />
}

/** The failure pair. Only ever for an actual failure — see AlertRow and Refusal. */
const FAILURE = {
  border: '1px solid var(--w-danger-line)',
  background: 'var(--w-danger-wash)',
}

/**
 * The one mark that means "this did not work" — shared with the toast, which
 * is the other place the product has to say so and used to say it with a tick.
 */
export function WarningGlyph({ size = 17, style }: { size?: number; style?: CSSProperties } = {}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      // The token, not the #e08a7d it was written as. That literal is within a
      // hair of --w-danger-ink in dark mode, which is the only mode it was ever
      // looked at in — and in light mode it is a pale salmon on a pale ground,
      // so the one mark that means "this did not work" was the faintest thing
      // in the row. Found when the toast started using it.
      stroke="var(--w-danger-ink)"
      strokeWidth="1.25"
      style={{ flex: 'none', marginTop: 1, ...style }}
      aria-hidden="true"
    >
      <path d="M10 2.8 18 16.8H2L10 2.8Z" strokeLinejoin="round" />
      <path d="M10 8v3.4" strokeLinecap="round" />
      <path d="M10 14.1h.01" strokeLinecap="round" />
    </svg>
  )
}

interface AlertRowProps {
  title: string
  children?: ReactNode
  /** "Try again" — the way out. An error with no action is a dead end. */
  action?: ReactNode
  style?: CSSProperties
}

/**
 * A write that rolled back, said inline and immediately above whatever failed.
 *
 * The title's job is to say **what state the thing is in now** — "the switch is
 * back where it was" — rather than only what went wrong. The control it belongs
 * to visibly returns to its old position at the same time; this row explains
 * the movement somebody has already seen.
 */
export function AlertRow({ title, children, action, style }: AlertRowProps) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '11px 13px',
        borderRadius: 11,
        ...FAILURE,
        ...style,
      }}
    >
      <WarningGlyph />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ font: '500 11px/1.6 Manrope,sans-serif', color: 'var(--w-danger-ink)' }}>{title}</span>
        {children && (
          <span
            style={{
              font: '400 11px/1.6 Manrope,sans-serif',
              color: 'var(--color-muted)',
              textWrap: 'pretty',
            }}
          >
            {children}
          </span>
        )}
      </div>
      {action}
    </div>
  )
}

/**
 * Something the product refuses, as a whole card in the failure pair.
 *
 * The action is the **route to the fix**, never a retry: pressing it again
 * cannot help, because nothing went wrong — the answer is no, and the useful
 * thing to offer is the place where that changes.
 */
export function Refusal({
  title,
  children,
  action,
  style,
}: {
  title: string
  children?: ReactNode
  action?: ReactNode
  style?: CSSProperties
}) {
  return (
    <div
      role="alert"
      className="fr"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 12,
        padding: '26px 20px',
        minHeight: 170,
        borderRadius: 14,
        ...FAILURE,
        ...style,
      }}
    >
      <span style={{ font: '600 13px Manrope,sans-serif', color: 'var(--w-danger-ink)' }}>{title}</span>
      {children && (
        <span
          style={{
            font: '400 11px/1.65 Manrope,sans-serif',
            color: 'var(--w-danger-ink)',
            opacity: 0.85,
            maxWidth: 340,
            textWrap: 'pretty',
          }}
        >
          {children}
        </span>
      )}
      {action}
    </div>
  )
}

/**
 * A section that didn't load, which **is not the reader's failure** — so it
 * does not wear the failure colour. Neutral card, the reassurance that nothing
 * is lost, a retry, and a reference for the one case where somebody asks us
 * what happened.
 */
export function LoadFailed({
  title = "This didn't load",
  children,
  action,
  reference,
  style,
}: {
  title?: string
  children?: ReactNode
  action?: ReactNode
  reference?: string
  style?: CSSProperties
}) {
  return (
    <div
      role="alert"
      className="fr"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 12,
        padding: '26px 20px',
        minHeight: 170,
        borderRadius: 14,
        border: '1px solid var(--w-line)',
        background: 'var(--w-pane)',
        boxShadow: 'var(--w-inset),var(--w-lift)',
        ...style,
      }}
    >
      <span style={{ font: '600 13px Manrope,sans-serif' }}>{title}</span>
      <span
        style={{
          font: '400 11px/1.65 Manrope,sans-serif',
          color: 'var(--color-muted)',
          maxWidth: 340,
          textWrap: 'pretty',
        }}
      >
        {children ?? 'Nothing is lost — the page just couldn’t read this. The rest of the app is fine.'}
      </span>
      {action}
      {reference && (
        <span className="text-data" style={{ fontSize: 10, color: 'var(--color-faint)' }}>
          ref {reference}
        </span>
      )}
    </div>
  )
}
